import { Box } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import React, { FC, useMemo } from 'react';

import { MESSAGES } from '../../../../constants';
import { assertUnreachable } from '../../../../util/guards';
import { IBotMessage, IBotTitleMessage } from '../../../common/messageCreators';
import { isHtmlHebrew } from '../../../util/language';
import { usePingLinkRef } from '../../hooks';
import { useDeepLinkClickListener } from '../../hooks/useDeepLinkClickListener';
import { useNavigateToArticleClickListener } from '../../hooks/useNavigateToArticleClickListener';
import { ExtraChatMessageProps } from '../Chat';
import { DirectionWrapper } from '../DirectionWrapper';
import { MessageBubble } from '../MessageBubble';
import { MessageItem } from '../MessageItem';
import { BotMessageIcon } from './BotMessageIcon';

export const BotMessage: FC<(IBotMessage | IBotTitleMessage) & ExtraChatMessageProps> = (props) => {
  const { id, icon, isFirstInGroup } = props;
  const contentHtml = getContentHtml(props);
  const linkPingRef = usePingLinkRef<HTMLDivElement>();
  const isRtl = useMemo(() => isHtmlHebrew(contentHtml), [contentHtml]);

  const dangerouslySetInnerHTML = React.useMemo(
    () => ({
      __html: sanitizeHtml(contentHtml),
    }),
    [contentHtml],
  );

  const onLinkClick = useNavigateToArticleClickListener();
  useDeepLinkClickListener(linkPingRef, onLinkClick);

  return (
    <MessageItem id={id} width="full">
      <MessageBubble isFirstInGroup={isFirstInGroup} icon={icon && <BotMessageIcon icon={icon} />}>
        <DirectionWrapper isRtl={isRtl}>
          <Box direction="vertical" whiteSpace="pre-wrap">
            <div
              ref={linkPingRef}
              dangerouslySetInnerHTML={dangerouslySetInnerHTML}
              data-hook="bot-message"
            />
          </Box>
        </DirectionWrapper>
      </MessageBubble>
    </MessageItem>
  );
};

function getContentHtml(message: IBotMessage | IBotTitleMessage): string {
  const { type } = message;

  switch (type) {
    case MESSAGES.BOT_MESSAGE:
      return message.html;
    case MESSAGES.TITLE_BOT_MESSAGE:
      return message.title;
    default:
      assertUnreachable(type);
  }
}
